import React, { useContext, useEffect, useRef, useState } from 'react';
import { defaultProvider, Provider } from '../../../app/models/Provider';
import useProviderService from '../../../app/services/hooks/useProviderService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import LazyLoading from '../../../components/LazyLoading';

import useSweetAlert from '../../../hooks/useSweetAlert';
import { Place } from '../../../app/models/Place';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import {
    RouteSegment,
    RouteSegmentDataForm,
    defaultRouteSegmentDataForm
} from '../../../app/models/RouteSegment';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';
import RouteSegmentFormContainer from './RouteSegmentFormContainer';
import { toast } from 'react-toastify';
import QuoteRouteSegmentFormContainer from './QuoteRouteSegmentFormContainer';
import {
    QuoteRouteSegmentDataForm,
    defaultQuoteRouteSegmentDataForm
} from '../../../app/models/QuoteRouteSegment';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';

interface Props {
    quoteRouteSegmentIdEdit: number;
    quoteId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const QuoteRouteSegmentEdit = ({
    quoteRouteSegmentIdEdit,
    quoteId,
    onSaved,
    onCancel,
    onError
}: Props) => {
    const {
        fetchingEditQuoteRouteSegment,
        editQuoteRouteSegment,
        fetchingUpdateQuoteRouteSegment,
        updateQuoteRouteSegment,
        fetchingShowRsToQuoteRouteSegment,
        showRsToQuoteRouteSegment
    } = useQuoteRouteSegmentService();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const [units, setUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [errorGetData, setErrorGetData] = useState('');
    const [hasToi, setHasToi] = useState(false);
    const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);

    const [quoteRouteSegment, setQuoteRouteSegment] = useState<QuoteRouteSegmentDataForm>({
        ...defaultQuoteRouteSegmentDataForm,
        ['quote_id']: quoteId,
        ['route_segment_id']: undefined,
        ['agreed_distance']: 0
    });

    const [quoteRouteSegmentEdit, setQuoteRouteSegmentEdit] = useState<QuoteRouteSegmentDataForm>({
        ...defaultQuoteRouteSegmentDataForm,
        ['quote_id']: quoteId,
        ['route_segment_id']: undefined,
        ['agreed_distance']: 0
    });
    const [errorFields, setErrorFields] = useState<any>();
    const warningOnRef = useRef<boolean>(true);

    useEffect(() => {
        edit();
    }, []);

    const edit = () => {
        editQuoteRouteSegment(quoteRouteSegmentIdEdit, {
            onSuccess: (response: ServiceResponse) => {
                setUnits(response.data.units);
                setCurrencies(response.data.currencies);
                setRouteSegments(response.data.route_segments);
                setQuoteRouteSegment(response.data.quote_route_segment);
                setQuoteRouteSegmentEdit(response.data.quote_route_segment);
                setHasToi(response.data.warning)
            },
            onError: (response: ServiceResponse) => {
                setErrorGetData(response.message);
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message,
                    message_list: response.data?.message_list ?? []
                });
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    useEffect(() => {
        if (
            quoteRouteSegment.route_segment_id &&
            quoteRouteSegment.route_segment_id == quoteRouteSegmentEdit.route_segment_id
        ) {
            console.log(quoteRouteSegmentEdit);
            setQuoteRouteSegment({
                ...quoteRouteSegment,
                ...defaultQuoteRouteSegmentDataForm,
                ...quoteRouteSegmentEdit
            });
        } else if (
            quoteRouteSegment.route_segment_id &&
            quoteRouteSegment.route_segment_id != quoteRouteSegmentEdit.route_segment_id
        ) {
            showRsToQuoteRouteSegment(quoteRouteSegment.route_segment_id, {
                onSuccess: (response: ServiceResponse) => {
                    setErrorFields(undefined);
                    setQuoteRouteSegment({
                        ...quoteRouteSegment,
                        ...defaultQuoteRouteSegmentDataForm,
                        ...response.data.route_segment
                    });
                }
            });
        }
    }, [quoteRouteSegment.route_segment_id]);

    const requestWarningConfirm = (nessage: string) => {

            useReactConfirmAlert().requestConfirmation({
                title: '¿Estás seguro?',
                message: nessage,
                buttons: {
                    confirmButton: {
                        label: 'Si, confirmar',
                        onClick: () => {
                            warningOnRef.current = false;
                            update();
                        }
                    },
                    cancelButton: {
                        label: 'No, cancelar',
                        onClick: () => {
                            setTimeout(() => {
                                useReactConfirmAlert().infoAlert({
                                    title: 'Cancelado',
                                    message: 'La opción ruta no se ha modificado.'
                                });
                            }, 0);
                            warningOnRef.current = true;
                            if (onError) onError();

                        }
                    }
                },
                onClickOutsideFunction: () => {
                    warningOnRef.current = true;
                }
            })
     
    } 

    const update = () => {
        
        if (showLoading) showLoading('loading', 'Actualizando opción de ruta...');
        updateQuoteRouteSegment(quoteRouteSegmentIdEdit, {...quoteRouteSegment, hasWarning: warningOnRef.current}, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                warningOnRef.current = true;
                toast.success(response.message);
                if (onSaved) onSaved();
            },
            onWarning: (response: ServiceResponse) => {
                if (hideLoading) hideLoading(); 
                requestWarningConfirm(response.message);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                warningOnRef.current = true;
                
                toast.error(response.message);
                if (onError) onError();
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                warningOnRef.current = true;
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingEditQuoteRouteSegment || fetchingShowRsToQuoteRouteSegment ? (
        <LazyLoading height="300" />
    ) : (
        <QuoteRouteSegmentFormContainer
            fetching={fetchingUpdateQuoteRouteSegment}
            action={update}
            cancel={cancel}
            quoteRouteSegmentForm={quoteRouteSegment}
            setQuoteRouteSegmentForm={setQuoteRouteSegment}
            routeSegments={routeSegments}
            currencies={currencies}
            units={units}
            errorFields={errorFields}
            has_toi={hasToi}
        />
    );
};

export default QuoteRouteSegmentEdit;
